import { useCodeDefinitionsAsOptions } from '../hooks/useCodeDefinitions';
import { getReactSelectCompactStyles } from '../utils/utils';
import { DropdownSelect } from './dropdown-select';

// Memoized once: react-select shallow-compares `styles` and re-styles on identity change.
// Compact = 28px chip-style control built for dense action rows (see ScheduleTypeSelect);
// the `small` variant's 24px control clips the value inside the drawers' text-sm/6 context.
const COMPACT_STYLES = getReactSelectCompactStyles();

/**
 * CodeDefinition category that backs a note model's `channel` choices.
 * - `NoteChannel`: the full set (clearance + connection notes)
 * - `HCONoteChannel`: the org-scoped subset (assignment, timecard, opportunity, need notes)
 */
export type NoteChannelCategory = 'NoteChannel' | 'HCONoteChannel';

export type NoteChannelConfig = {
  category: NoteChannelCategory;
  // Channel code pre-selected in the composer. Mirrors the backend model default so a
  // note saved without touching the picker lands where it always has.
  defaultValue: string;
};

// Backend defaults: `BaseNote.channel` → DEFAULT_NOTE_CHANNEL (ALL); the HCO-scoped note
// models override the default to HCO.
export const FULL_NOTE_CHANNELS: NoteChannelConfig = {
  category: 'NoteChannel',
  defaultValue: 'ALL',
};
export const HCO_NOTE_CHANNELS: NoteChannelConfig = {
  category: 'HCONoteChannel',
  defaultValue: 'HCO',
};

type NoteChannelSelectProps = {
  category: NoteChannelCategory;
  value: string | undefined;
  onChange: (channel: string) => void;
  disabled?: boolean;
  inputId?: string;
};

/**
 * "Visible to" picker for a note's channel. Options come from CodeDefinitions so the
 * labels stay data-driven; the caller decides whether to render it at all (staff only).
 */
export function NoteChannelSelect({
  category,
  value,
  onChange,
  disabled = false,
  inputId = 'note-channel-select',
}: NoteChannelSelectProps) {
  const { data: options, isLoading } = useCodeDefinitionsAsOptions(category);

  return (
    <div className="tw:flex tw:items-center tw:gap-2 tw:min-w-0" data-testid="note-channel-select">
      {/* Plain <label>: the catalyst Label requires a Field/Fieldset parent, and this
          picker is rendered inside plain action rows (composer + note edit form). */}
      <label
        htmlFor={inputId}
        className="tw:text-xs tw:font-medium tw:text-zinc-600 tw:whitespace-nowrap"
      >
        Visible to
      </label>
      {/* w-fit: compact styles disable value truncation so the control sizes to the
          widest option label and the menu matches it. */}
      <div className="tw:w-fit">
        <DropdownSelect
          inputId={inputId}
          allOptions={options}
          value={value}
          onChange={(option) => {
            if (option && !Array.isArray(option)) onChange(option.value);
          }}
          isDisabled={disabled}
          isLoading={isLoading}
          placeholder="Select audience..."
          styles={COMPACT_STYLES}
          isSearchable={false}
          menuPlacement="auto"
        />
      </div>
    </div>
  );
}
